import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import {
  CheckCircle,
  Plus,
  Search,
  RefreshCw,
  Clock,
  XCircle,
  Calendar,
  Eye
} from 'lucide-react';

const CONFIRMATION_TYPES = [
  { value: 'TIME', label: 'Time/Labor Confirmation' },
  { value: 'MILESTONE', label: 'Milestone Confirmation' },
  { value: 'PROGRESS', label: 'Progress Update' },
  { value: 'GOODS_MOVEMENT', label: 'Goods Movement' },
  { value: 'FINAL', label: 'Final Confirmation' }
];

export default function Confirmations() {
  const [confirmations, setConfirmations] = useState([]);
  const [activities, setActivities] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedConfirmation, setSelectedConfirmation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterNetwork, setFilterNetwork] = useState('__all__');
  const [filterType, setFilterType] = useState('__all__');

  const [formData, setFormData] = useState({
    activity_id: '',
    confirmation_type: 'TIME',
    confirmation_date: new Date().toISOString().split('T')[0],
    actual_work: 0,
    remaining_work: 0,
    progress_percent: 0,
    is_final_confirmation: false,
    text: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterNetwork && filterNetwork !== '__all__') params.network_id = filterNetwork;
      if (filterType && filterType !== '__all__') params.confirmation_type = filterType;
      
      const [confirmationsRes, activitiesRes, networksRes] = await Promise.all([
        psAPI.getConfirmations(params),
        psAPI.getActivities(),
        psAPI.getNetworks()
      ]);
      setConfirmations(confirmationsRes.data);
      setActivities(activitiesRes.data);
      setNetworks(networksRes.data);
    } catch (error) {
      toast.error('Failed to load confirmations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filterNetwork, filterType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleCreate = async () => {
    try {
      const payload = {
        ...formData,
        actual_work: parseFloat(formData.actual_work) || 0,
        remaining_work: parseFloat(formData.remaining_work) || 0,
        progress_percent: parseFloat(formData.progress_percent) || 0
      };
      
      await psAPI.createConfirmation(payload);
      toast.success('Confirmation posted successfully');
      setShowCreateDialog(false);
      setFormData({
        activity_id: '',
        confirmation_type: 'TIME',
        confirmation_date: new Date().toISOString().split('T')[0],
        actual_work: 0,
        remaining_work: 0,
        progress_percent: 0,
        is_final_confirmation: false,
        text: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post confirmation');
    }
  };

  const handleCancel = async (confirmationId) => {
    if (!window.confirm('Are you sure you want to cancel this confirmation?')) return;
    
    try {
      await psAPI.cancelConfirmation(confirmationId);
      toast.success('Confirmation cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel confirmation');
    }
  };

  const handleView = async (confirmation) => {
    try {
      const response = await psAPI.getConfirmation(confirmation.confirmation_id);
      setSelectedConfirmation(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load confirmation details');
    }
  };

  const filteredConfirmations = confirmations.filter(c => {
    const matchesSearch = !searchQuery || 
      c.confirmation_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.activity_id?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const getTypeBadge = (type) => {
    const variants = {
      'TIME': 'bg-orange-100 text-orange-700',
      'MILESTONE': 'bg-orange-100 text-orange-700',
      'PROGRESS': 'bg-emerald-100 text-emerald-700',
      'GOODS_MOVEMENT': 'bg-orange-100 text-orange-700',
      'FINAL': 'bg-red-100 text-red-700'
    };
    return <Badge className={variants[type] || 'bg-gray-100'}>{type}</Badge>;
  };

  const getStatusBadge = (status) => {
    if (status === 'CANCELLED') {
      return <Badge className="bg-red-100 text-red-700">Cancelled</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-700">Posted</Badge>;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Confirmations</h1>
            <p className="text-sm text-gray-500 mt-1">Post and manage activity confirmations</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowCreateDialog(true)} data-testid="post-confirmation-btn">
              <Plus className="w-4 h-4 mr-2" />
              Post Confirmation
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search confirmations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-confirmations"
                  />
                </div>
              </div>
              <div className="w-48">
                <Select value={filterNetwork} onValueChange={setFilterNetwork}>
                  <SelectTrigger data-testid="filter-network">
                    <SelectValue placeholder="All Networks" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Networks</SelectItem>
                    {networks.map(n => (
                      <SelectItem key={n.network_id} value={n.network_id}>{n.network_id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger data-testid="filter-type">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Types</SelectItem>
                    {CONFIRMATION_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Confirmations List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Confirmations ({filteredConfirmations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-6 h-6 animate-spin text-orange-600" />
              </div>
            ) : filteredConfirmations.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No confirmations found</p>
                <Button onClick={() => setShowCreateDialog(true)} variant="link" className="mt-2">
                  Post your first confirmation
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3">Confirmation ID</th>
                      <th className="text-left p-3">Activity</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Date</th>
                      <th className="text-right p-3">Actual Work</th>
                      <th className="text-right p-3">Progress</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredConfirmations.map((conf) => (
                      <tr key={conf.confirmation_id} className="border-b hover:bg-gray-50" data-testid={`confirmation-row-${conf.confirmation_id}`}>
                        <td className="p-3 font-medium text-orange-600">{conf.confirmation_id}</td>
                        <td className="p-3">{conf.activity_id}</td>
                        <td className="p-3">{getTypeBadge(conf.confirmation_type)}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {conf.confirmation_date}
                          </div>
                        </td>
                        <td className="p-3 text-right">{conf.actual_work || 0}h</td>
                        <td className="p-3 text-right">{conf.progress_percent || 0}%</td>
                        <td className="p-3">{getStatusBadge(conf.confirmation_status)}</td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleView(conf)}
                              data-testid={`view-${conf.confirmation_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {conf.confirmation_status !== 'CANCELLED' && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleCancel(conf.confirmation_id)}
                                data-testid={`cancel-${conf.confirmation_id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Post Confirmation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Activity *</Label>
                <Select value={formData.activity_id} onValueChange={(v) => setFormData({ ...formData, activity_id: v })}>
                  <SelectTrigger data-testid="select-activity">
                    <SelectValue placeholder="Select activity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select activity</SelectItem>
                    {activities.filter(a => a.activity_status !== 'CREATED').map(a => (
                      <SelectItem key={a.activity_id} value={a.activity_id}>
                        {a.activity_id} - {a.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Confirmation Type</Label>
                <Select value={formData.confirmation_type} onValueChange={(v) => setFormData({ ...formData, confirmation_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONFIRMATION_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Confirmation Date</Label>
              <Input
                type="date"
                value={formData.confirmation_date}
                onChange={(e) => setFormData({ ...formData, confirmation_date: e.target.value })}
                data-testid="input-confirmation-date"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Actual Work (hours)</Label>
                <Input
                  type="number"
                  value={formData.actual_work}
                  onChange={(e) => setFormData({ ...formData, actual_work: e.target.value })}
                  min="0"
                  step="0.5"
                  data-testid="input-actual-work"
                />
              </div>
              <div className="space-y-2">
                <Label>Remaining Work</Label>
                <Input
                  type="number"
                  value={formData.remaining_work}
                  onChange={(e) => setFormData({ ...formData, remaining_work: e.target.value })}
                  min="0"
                  step="0.5"
                />
              </div>
              <div className="space-y-2">
                <Label>Progress %</Label>
                <Input
                  type="number"
                  value={formData.progress_percent}
                  onChange={(e) => setFormData({ ...formData, progress_percent: e.target.value })}
                  min="0"
                  max="100"
                  data-testid="input-progress"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_final"
                checked={formData.is_final_confirmation}
                onChange={(e) => setFormData({ ...formData, is_final_confirmation: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="is_final" className="cursor-pointer">Final Confirmation</Label>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreate}
              disabled={!formData.activity_id || formData.activity_id === '__none__'}
              data-testid="submit-confirmation"
            >
              Post Confirmation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirmation Details</DialogTitle>
          </DialogHeader>
          {selectedConfirmation && (
            <div className="space-y-4 py-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-500">Confirmation ID</Label>
                  <div className="font-medium">{selectedConfirmation.confirmation_id}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Status</Label>
                  <div>{getStatusBadge(selectedConfirmation.confirmation_status)}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Activity</Label>
                  <div className="font-medium">{selectedConfirmation.activity_id}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Type</Label>
                  <div>{getTypeBadge(selectedConfirmation.confirmation_type)}</div>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Confirmation Date</Label>
                    <p className="p-2 bg-gray-50 rounded">{selectedConfirmation.confirmation_date}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Posting Date</Label>
                    <p className="p-2 bg-gray-50 rounded">{selectedConfirmation.posting_date || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Actual Work</Label>
                    <p className="p-2 bg-gray-50 rounded font-medium">{selectedConfirmation.actual_work || 0} hours</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Remaining Work</Label>
                    <p className="p-2 bg-gray-50 rounded">{selectedConfirmation.remaining_work || 0} hours</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Progress</Label>
                    <p className="p-2 bg-gray-50 rounded font-medium">{selectedConfirmation.progress_percent || 0}%</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-gray-500">Final Confirmation</Label>
                  <p className="p-2 bg-gray-50 rounded">
                    {selectedConfirmation.is_final_confirmation ? 
                      <Badge className="bg-emerald-100 text-emerald-700">Yes</Badge> : 
                      <Badge className="bg-gray-100 text-gray-700">No</Badge>
                    }
                  </p>
                </div>

                {selectedConfirmation.text && (
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Notes</Label>
                    <p className="p-2 bg-gray-50 rounded whitespace-pre-wrap">{selectedConfirmation.text}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Created At</Label>
                    <p className="text-sm">{selectedConfirmation.created_at ? new Date(selectedConfirmation.created_at).toLocaleString() : '-'}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Confirmed By</Label>
                    <p className="text-sm">{selectedConfirmation.confirmed_by || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
